import type { Role } from "../lib/permissions";

/** Tipos da API v1 (espelham os schemas zod de apps/api). */
export interface User {
  id: string;
  email: string;
  name: string | null;
}

/** Usuário da sessão; `isPlatformAdmin` = fundador (vê a Administração). */
export interface Me extends User {
  isPlatformAdmin: boolean;
  /** Assistente de IA liberado para este usuário (#20). */
  assistantEnabled: boolean;
  /** Comprovantes ligados no ambiente (#37): sem o bucket, anexar some. */
  attachmentsEnabled: boolean;
}

/** Sessão do usuário ("Aparelhos conectados"); datas em ms. `current` = a deste navegador. */
export interface UserSession {
  id: string;
  userAgent: string | null;
  ip: string | null;
  createdAt: number;
  lastSeenAt: number;
  expiresAt: number;
  current: boolean;
}

export type PlatformInvitationStatus = "pending" | "accepted" | "expired" | "revoked";

export interface PlatformInvitation {
  id: string;
  email: string;
  status: PlatformInvitationStatus;
  invitedByEmail: string | null;
  expiresAt: number;
  acceptedAt: number | null;
  revokedAt: number | null;
  lastSentAt: number;
  sendCount: number;
  createdAt: number;
}

export interface Workspace {
  id: string;
  name: string;
  defaultCurrency: string;
  timezone: string;
  role: Role;
  createdAt: number;
}

export type AccountType = "checking" | "savings" | "credit_card" | "cash" | "investment" | "other";

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  currency: string;
  openingBalanceMinor: number;
  openingDate: string;
  balanceMinor: number;
  archivedAt: number | null;
}

export type CategoryKind = "income" | "expense";

export interface Category {
  id: string;
  name: string;
  kind: CategoryKind;
  parentId: string | null;
  color: string | null;
  icon: string | null;
  archivedAt: number | null;
}

export type TransactionType = "income" | "expense" | "transfer";
export type TransactionStatus = "pending" | "cleared";

export interface Transaction {
  id: string;
  accountId: string;
  categoryId: string | null;
  type: TransactionType;
  transferDirection: "out" | "in" | null;
  transferGroupId: string | null;
  amountMinor: number;
  currency: string;
  date: string;
  description: string;
  notes: string | null;
  status: TransactionStatus;
  recurrenceId: string | null;
  tags: string[];
  /** Comprovante anexado (#37): só tipo e tamanho. A imagem sai por `api/attachments`. */
  attachment: { contentType: string; sizeBytes: number } | null;
}

export interface TransactionPage {
  transactions: Transaction[];
  nextCursor: string | null;
}

export interface CurrencyTotals {
  incomeMinor: number;
  expenseMinor: number;
  netMinor: number;
}

/** Totais de uma moeda que não é a do workspace: a API nunca soma moedas diferentes. */
export interface OtherCurrencyTotals extends CurrencyTotals {
  currency: string;
}

export interface CategoryTotal {
  categoryId: string;
  name: string;
  kind: CategoryKind;
  totalMinor: number;
  children: Array<{ categoryId: string; name: string; totalMinor: number }>;
}

/** Totais e `byCategory` na moeda do workspace; as demais moedas com movimento em `otherCurrencies`. */
export interface MonthlySummary extends CurrencyTotals {
  month: string;
  currency: string;
  byCategory: CategoryTotal[];
  otherCurrencies: Array<OtherCurrencyTotals & { byCategory: CategoryTotal[] }>;
}

export interface BudgetStatus {
  categoryId: string;
  categoryName: string;
  budgetedMinor: number;
  spentMinor: number;
  remainingMinor: number;
  percentUsed: number;
}

export interface CashFlowMonth extends CurrencyTotals {
  month: string;
  otherCurrencies: OtherCurrencyTotals[];
}

export interface Recurrence {
  id: string;
  frequency: "weekly" | "monthly" | "yearly";
  dayOfMonth: number | null;
  weekday: number | null;
  month: number | null;
  startDate: string;
  endDate: string | null;
  accountId: string;
  categoryId: string | null;
  type: CategoryKind;
  amountMinor: number;
  description: string;
  /** Última data já transformada em lançamento pelo cron (datas até ela não são "próximas"). */
  lastGeneratedDate?: string | null;
}

export const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  checking: "Conta corrente",
  savings: "Poupança",
  credit_card: "Cartão de crédito",
  cash: "Dinheiro",
  investment: "Investimento",
  other: "Outra",
};

export interface Member {
  userId: string;
  email: string;
  name: string | null;
  role: Role;
  createdAt: number;
}

export interface Invitation {
  id: string;
  email: string;
  role: Role;
  expiresAt: number;
  createdAt: number;
}

export type KeyScope = "read" | "write";

export interface ApiKey {
  id: string;
  name: string;
  displayPrefix: string;
  workspaceId: string;
  scope: KeyScope;
  expiresAt: number | null;
  lastUsedAt: number | null;
  createdAt: number;
}

export interface Agent {
  id: string;
  clientId: string;
  clientName: string;
  workspaceId: string;
  scope: string[];
  createdAt: number;
}

export interface AuditEntry {
  id: string;
  actorType: "user" | "agent" | "system";
  actorUserId: string | null;
  actorClientId: string | null;
  actorApiKeyId: string | null;
  action: string;
  entity: string;
  entityId: string | null;
  diff: unknown;
  createdAt: number;
}

export const SCOPE_LABEL: Record<KeyScope, string> = {
  read: "Somente leitura",
  write: "Leitura e escrita",
};
